import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import { TRYON_SERVER_URL } from "../config";
import { fetchUser } from "../Utils/UserUtils";
import { setUserData } from "../store/AuthActions";
import promptBestValues from "./prompt_best_values.json";
import { useNavigate } from "react-router-dom";
import { CopyIcon, RotateCcwIcon, SearchIcon, WandSparklesIcon, XIcon } from "lucide-react";

const MODEL_META = promptBestValues.model_meta || {};
const PROMPT_CREDITS = 1;

const INITIAL_PERSONA_STATE = {
  gender: "",
  age: "",
  customAge: "",
  ethnicity: "",
  complexion: "",
  hairStyle: "",
  hairType: "",
  faceShape: "",
  eyeType: "",
  eyeColor: "",
  expression: "",
  hair_color: "",
  pose: "",
  outfit: "",
  lighting: "",
  background: "",
  fabric_description: "",
};

const normalizePersonaState = (prev, updates) => {
  const next = { ...prev, ...updates };

  if (
    updates.age &&
    updates.age !== "Custom" &&
    !AGE_OPTIONS.includes(updates.age)
  ) {
    next.customAge = updates.age;
    next.age = "Custom";
  }

  if (updates.customAge !== undefined && updates.customAge !== "") {
    next.age = "Custom";
  }

  if (next.age && next.age !== "Custom" && AGE_OPTIONS.includes(next.age)) {
    next.customAge = updates.customAge !== undefined ? updates.customAge : "";
  }

  return next;
};

const buildPresetList = (key, baseOptions = [], limit = 12) => {
  const curated = Array.isArray(promptBestValues?.[key])
    ? promptBestValues[key]
    : [];
  const seen = new Set();
  const presets = [];

  const addOptions = (list) => {
    list.forEach((option) => {
      if (!option) return;
      const trimmed = typeof option === "string" ? option.trim() : option;
      if (trimmed && !seen.has(trimmed)) {
        presets.push(trimmed);
        seen.add(trimmed);
      }
    });
  };

  addOptions(curated);
  addOptions(baseOptions);

  return limit > 0 ? presets.slice(0, limit) : presets;
};

const GENDER_OPTIONS = promptBestValues.gender || [];
const AGE_OPTIONS = Array.from(
  new Set([...(promptBestValues.age || []), "Custom"])
);
const ETHNICITY_OPTIONS = promptBestValues.ethnicity || [];
const COMPLEXION_OPTIONS = promptBestValues.complexion || [];
const EXPRESSION_OPTIONS = promptBestValues.expression || [];
const EYE_TYPE_OPTIONS = promptBestValues.eye_type || [];
const EYE_COLOR_OPTIONS = promptBestValues.eye_color || [];
const HAIR_STYLE_LIST = promptBestValues.hair_style || [];
const HAIR_TYPE_OPTIONS = promptBestValues.hair_type || [];
const HAIR_COLOR_OPTIONS = promptBestValues.hair_color || [];
const FABRIC_DESCRIPTION_OPTIONS = promptBestValues.fabric_description || [];
const HAIR_STYLE_OPTIONS = {
  Male: HAIR_STYLE_LIST,
  Female: HAIR_STYLE_LIST,
  Other: HAIR_STYLE_LIST,
};
const OUTFIT_OPTIONS = promptBestValues.outfit || [];
const FACE_SHAPE_LIST = promptBestValues.face_shape || [];
const FACE_SHAPE_OPTIONS = {
  Male: FACE_SHAPE_LIST,
  Female: FACE_SHAPE_LIST,
  Other: FACE_SHAPE_LIST,
};
const POSE_PRESETS = buildPresetList("pose", [], 14);
const LIGHTING_PRESETS = buildPresetList("lighting", [], 14);
const BACKGROUND_PRESETS = buildPresetList("background", [], 14);

const SearchableDropdown = ({
  label,
  description,
  options = [],
  value,
  onSelect,
  placeholder = "Search",
  emptyText = "No matches found.",
  chip = true,
  allowCustom = true,
}) => {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option?.toLowerCase().includes(q));
  }, [query, options]);

  const closeDropdown = () => setOpen(false);

  const handleSelect = (option) => {
    onSelect(option);
    if (!allowCustom) {
      setQuery(option || "");
    }
    closeDropdown();
  };

  const handleClear = () => {
    onSelect("");
    setQuery("");
    closeDropdown();
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setQuery(nextValue);
    setOpen(true);
    if (allowCustom) {
      onSelect(nextValue);
    }
  };

  return (
    <div className="space-y-1.5 sm:space-y-2">
      {label && (
        <p className="font-Mulish text-sm text-graphite">{label}</p>
      )}
      {description && <p className="font-Mulish text-sm text-graphite">{description}</p>}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => {
            setTimeout(() => closeDropdown(), 120);
          }}
          placeholder={placeholder}
          className="w-full rounded-lg border border-hairline bg-white py-2 pl-9 pr-16 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
        />
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-graphite" />
        {value && (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-primary-50 px-3 py-1 font-Mulish text-sm text-primary-700 hover:bg-primary-100"
          >
            Clear
          </button>
        )}
        {open && (
          <div className="absolute left-0 right-0 z-20 mt-2 max-h-56 overflow-y-auto rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
            <div className="flex items-center justify-between border-b border-hairline bg-primary-50 px-4 py-2 font-Mulish text-sm text-graphite">
              <span>Matches</span>
              <span>{filteredOptions.length}</span>
            </div>
            {filteredOptions.length ? (
              <ul className="divide-y divide-hairline">
                {filteredOptions.map((option) => (
                  <li key={option}>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelect(option)}
                      className={`w-full px-4 py-2 text-left font-Mulish text-sm ${
                        value === option
                          ? "bg-primary-50 font-semibold text-secondary"
                          : "text-graphite hover:bg-hover-tint"
                      }`}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-400">{emptyText}</div>
            )}
          </div>
        )}
      </div>
      {chip && value && (
        <div className="mt-2 inline-flex items-center rounded-full bg-primary-50 px-3 py-1.5 font-Mulish text-sm text-secondary">
          {value}
        </div>
      )}
    </div>
  );
};

const FashionPromptBuilder = ({
  isModal = false,
  setShowFashionPromptBuilder,
  onSelectPrompt,
  showFashionPromptBuilderRef,
}) => {
  const accessToken = useSelector(selectAccessToken);
  const authenticated = useSelector(selectAuthenticated);
  const userData = JSON.parse(useSelector(selectUserData) || "null");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, setState] = useState({
    ...INITIAL_PERSONA_STATE,
  });
  const [activeModelKey, setActiveModelKey] = useState("nano_banana");
  const [finalPrompt, setFinalPrompt] = useState("");
  const [aiEnriched, setAiEnriched] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const hairStyles =
    HAIR_STYLE_OPTIONS[state.gender] || HAIR_STYLE_OPTIONS.Other;
  const faceShapes =
    FACE_SHAPE_OPTIONS[state.gender] || FACE_SHAPE_OPTIONS.Other;

  const activeModelMeta = MODEL_META[activeModelKey];

  useEffect(() => {
    setState({ ...INITIAL_PERSONA_STATE });
    setAiEnriched(false);
    setCopied(false);
    setFinalPrompt("");
    setError("");
    setShowGeneratingModal(false);
    setSuccessMessage("");
  }, [activeModelKey]);

  useEffect(() => {
    if (!successMessage) return;
    const timeout = setTimeout(() => setSuccessMessage(""), 2500);
    return () => clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    if (finalPrompt) {
      setFinalPrompt(finalPrompt);
    }
  }, [finalPrompt]);

  useEffect(() => {
    if (!isModal) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowFashionPromptBuilder(false);
      }
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isModal, setShowFashionPromptBuilder]);

  const handleSelect = (field, value) => {
    setError("");
    setAiEnriched(false);
    setCopied(false);
    setFinalPrompt("");
    setState((prev) => {
      const updates = {};
      if (field === "gender") {
        updates.gender = value;
        updates.hairStyle = "";
        updates.hairType = "";
        updates.hair_color = "";
        updates.faceShape = "";
      } else if (field === "age") {
        updates.age = value;
        if (value !== "Custom") {
          updates.customAge = "";
        }
      } else if (field === "customAge") {
        updates.customAge = value;
        updates.age = "Custom";
      } else {
        updates[field] = value;
      }
      return normalizePersonaState(prev, updates);
    });
  };

  const handleCopyPrompt = async (promptToCopy) => {
    const prompt = promptToCopy ?? finalPrompt;
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("Failed to copy prompt", e);
    }
  };

  const createParameters = () => {
    const parameters = {
      gender: state.gender,
      age: state.age === "Custom" ? state.customAge : state.age,
      model_family: activeModelKey,
    };

    if (state.ethnicity) parameters.ethnicity = state.ethnicity;
    if (state.complexion) parameters.complexion = state.complexion;
    if (state.hairStyle) parameters.hair_style = state.hairStyle;
    if (state.hairType) parameters.hair_type = state.hairType;
    if (state.hair_color) parameters.hair_color = state.hair_color;
    if (state.faceShape) parameters.face_shape = state.faceShape;
    if (state.eyeType) parameters.eye_type = state.eyeType;
    if (state.eyeColor) parameters.eye_color = state.eyeColor;
    if (state.expression) parameters.expression = state.expression;
    if (state.fabric_description)
      parameters.fabric_description = state.fabric_description;

    return parameters;
  };

  const handleGeneratePrompt = async () => {
    setError("");
    if (!state.gender) {
      setError("Please select a gender to start.");
      return;
    }
    if (!state.age || (state.age === "Custom" && !state.customAge)) {
      setError("Please select an age or provide a custom age.");
      return;
    }
    if (!accessToken) {
      setError("Access token missing. Please log in and try again.");
      return;
    }

    const balance = Number(userData?.credits);
    if (!Number.isFinite(balance) || balance < PROMPT_CREDITS) {
      navigate(`/credits?buy_credits=true&num_of_credits=${PROMPT_CREDITS}`);
      return;
    }

    setAiEnriched(false);

    try {
      setShowGeneratingModal(true);
      setLoading(true);
      const response = await fetch(
        `${TRYON_SERVER_URL}/api/v1/prompt/generate/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ parameters: createParameters() }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        const apiPrompt =
          data?.prompt ||
          data?.choices?.[0]?.message?.content ||
          data?.choices?.[0]?.text ||
          "";

        if (apiPrompt) {
          setFinalPrompt(apiPrompt);
          setAiEnriched(true);
          setCopied(false);
          setSuccessMessage("Prompt generated successfully");
          const user = await fetchUser(accessToken);
          if (user.status === "success") {
            dispatch(setUserData(JSON.stringify(user.data)));
          }
        } else {
          setError("No prompt returned. Try adjusting your selections.");
        }
      } else if (data?.error === "Insufficient credits") {
        navigate(`/credits?buy_credits=true&num_of_credits=${PROMPT_CREDITS}`);
      } else {
        setError(data?.error || "The prompt could not be written. Try again.");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
      setShowGeneratingModal(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate("/signin");
  }

  const content = (
    <div className={isModal ? (authenticated === true ? 'w-full h-full' : 'w-full flex items-center justify-center') : 'min-h-[calc(100vh-5rem)] w-full overflow-y-auto bg-ground'}>
      {authenticated === null ?
        <div className="flex flex-wrap gap-2 rounded-lg p-2 sm:p-4 w-full">
            <div className="w-full shadow rounded-lg bg-white animate-pulse">
                <div className="bg-slate-200 rounded-lg h-48 sm:h-64 md:h-72 col-span-2 m-2 sm:m-4"></div>
                <div className="bg-slate-200 rounded-lg h-48 sm:h-64 md:h-72 col-span-2 m-2 sm:m-4"></div>
                <div className="bg-slate-200 rounded-lg h-48 sm:h-64 md:h-72 col-span-2 m-2 sm:m-4"></div>
            </div>
        </div>
        :authenticated ?
        
          (<>
          <div className={`mx-auto w-full ${isModal ? "px-6 py-6" : "max-w-5xl px-4 py-6 sm:px-6"}`}>
            {!isModal && (
              <h2 className="mb-6 font-Mulish font-semibold text-lg text-secondary">Fashion Prompt Builder</h2>
            )}
            <section className="pb-6">
              <div className="grid items-start gap-4 lg:grid-cols-12 lg:gap-6">
                <div className="space-y-1.5 lg:col-span-7">
                  <h2 className="flex flex-wrap items-center gap-2 font-Mulish text-lg font-semibold text-secondary">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 font-Mulish text-sm font-semibold text-secondary">
                      1
                    </span>
                    <span>Choose a look</span>
                  </h2>
                  <p className="font-Mulish text-sm text-graphite">
                    Pick a look, then add the person and the scene.
                  </p>
                </div>
                <div className="flex flex-col gap-3 lg:col-span-5">
                  <div>
                    <label
                      htmlFor="model-selector"
                      className="mb-2 block font-Mulish text-sm text-graphite"
                    >
                      Look
                    </label>
                    <select
                      id="model-selector"
                      value={activeModelKey}
                      onChange={(event) => {
                        const selectedModel = event.target.value;
                        setActiveModelKey(selectedModel);
                        setCopied(false);
                        setFinalPrompt("");
                      }}
                      className="w-full rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                    >
                      {Object.entries(MODEL_META).map(([key, model]) => (
                        <option key={key} value={key}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                    <span className="shrink-0 rounded-full bg-primary-50 px-3 py-1.5 font-Mulish text-sm text-secondary">
                      {activeModelMeta?.label}
                    </span>
                    <span className="font-Mulish text-sm text-graphite">
                      {activeModelMeta?.description}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid items-start gap-8 border-t border-hairline pt-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(16rem,1fr)]">
              <div>
                <div className="space-y-6">
                  <section className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                      <h2 className="flex flex-wrap items-center gap-2 font-Mulish font-semibold text-secondary">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 font-Mulish text-sm font-semibold text-secondary">
                          2
                        </span>
                        <span>Persona Basics</span>
                      </h2>
                      <span className="font-Mulish text-sm text-graphite">
                        Required fields
                      </span>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <SearchableDropdown
                        label="Gender"
                        description="Select the core persona gender."
                        options={GENDER_OPTIONS}
                        value={state.gender}
                        onSelect={(option) => handleSelect("gender", option)}
                        placeholder="Search genders"
                      />
                      <div className="space-y-2">
                        <SearchableDropdown
                          label="Age"
                          description="Choose an age range or enter a custom value."
                          options={AGE_OPTIONS}
                          value={state.age}
                          onSelect={(option) => handleSelect("age", option)}
                          placeholder="Search age ranges"
                        />
                        {state.age === "Custom" && (
                          <div className="space-y-2">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              placeholder="Enter custom age"
                              value={state.customAge}
                              onChange={(e) =>
                                handleSelect("customAge", e.target.value)
                              }
                              className="w-full rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200 sm:w-32"
                            />
                            {state.customAge && (
                              <p className="font-Mulish text-sm text-graphite">
                                Custom age set to{" "}
                                <span className="font-semibold text-secondary">
                                  {state.customAge}
                                </span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <SearchableDropdown
                        label="Ethnicity"
                        description="Refine the persona's cultural background."
                        options={ETHNICITY_OPTIONS}
                        value={state.ethnicity}
                        onSelect={(option) => handleSelect("ethnicity", option)}
                        placeholder="Search ethnicities"
                      />
                      <SearchableDropdown
                        label="Complexion"
                        description="Choose a skin tone that best fits the look."
                        options={COMPLEXION_OPTIONS}
                        value={state.complexion}
                        onSelect={(option) => handleSelect("complexion", option)}
                        placeholder="Search complexions"
                      />
                    </div>
                  </section>

                  <section className="space-y-4 border-t border-hairline pt-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                      <h2 className="flex flex-wrap items-center gap-2 font-Mulish font-semibold text-secondary">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 font-Mulish text-sm font-semibold text-secondary">
                          3
                        </span>
                        <span>Features & Styling</span>
                      </h2>
                      <span className="font-Mulish text-sm text-graphite">
                        Optional refinements
                      </span>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <SearchableDropdown
                        label="Hair Style"
                        description="Find a hair profile that complements your persona."
                        options={hairStyles}
                        value={state.hairStyle}
                        onSelect={(option) => handleSelect("hairStyle", option)}
                        placeholder="Search hair styles"
                      />
                      <SearchableDropdown
                        label="Hair Texture"
                        description="Choose a hair texture or type to fine-tune the look."
                        options={HAIR_TYPE_OPTIONS}
                        value={state.hairType}
                        onSelect={(option) => handleSelect("hairType", option)}
                        placeholder="Search hair textures"
                      />
                      <SearchableDropdown
                        label="Hair Color"
                        description="Select a hair color or tone for the persona."
                        options={HAIR_COLOR_OPTIONS}
                        value={state.hair_color}
                        onSelect={(option) => handleSelect("hair_color", option)}
                        placeholder="Search hair colors"
                      />
                      <SearchableDropdown
                        label="Face Shape"
                        description="Choose a face structure to guide the model details."
                        options={faceShapes}
                        value={state.faceShape}
                        onSelect={(option) => handleSelect("faceShape", option)}
                        placeholder="Search face shapes"
                      />
                      <SearchableDropdown
                        label="Eye Type"
                        description="Dial in the eye shape or mood you prefer."
                        options={EYE_TYPE_OPTIONS}
                        value={state.eyeType}
                        onSelect={(option) => handleSelect("eyeType", option)}
                        placeholder="Search eye types"
                      />
                      <SearchableDropdown
                        label="Eye Color"
                        description="Select a color palette to define the eye tone."
                        options={EYE_COLOR_OPTIONS}
                        value={state.eyeColor}
                        onSelect={(option) => handleSelect("eyeColor", option)}
                        placeholder="Search eye colors"
                      />
                      <SearchableDropdown
                        label="Outfit"
                        description="Search an outfit or pick one from the list below."
                        options={OUTFIT_OPTIONS}
                        value={state.outfit}
                        onSelect={(option) => handleSelect("outfit", option)}
                        placeholder="Search outfits"
                      />
                      <SearchableDropdown
                        label="Fabric Description"
                        description="Highlight key fabrics or materials featured in the outfit."
                        options={FABRIC_DESCRIPTION_OPTIONS}
                        value={state.fabric_description}
                        onSelect={(option) =>
                          handleSelect("fabric_description", option)
                        }
                        placeholder="Search fabric descriptors"
                      />
                      <SearchableDropdown
                        label="Expression"
                        description="Set the mood by picking a facial expression."
                        options={EXPRESSION_OPTIONS}
                        value={state.expression}
                        onSelect={(option) => handleSelect("expression", option)}
                        placeholder="Search expressions"
                      />
                    </div>
                  </section>
                </div>

                <section className="mt-6 space-y-4 border-t border-hairline pt-6">
                  <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
                    <h2 className="flex flex-wrap items-center gap-2 font-Mulish font-semibold text-secondary">
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 font-Mulish text-sm font-semibold text-secondary">
                        4
                      </span>
                      <span>Scene & Mood</span>
                    </h2>
                    <span className="font-Mulish text-sm text-graphite">
                      Context options
                    </span>
                  </div>
                  <div className="flex justify-between flex-col gap-3 sm:gap-4">
                    <div className="space-y-3 sm:space-y-4">
                      <SearchableDropdown
                        label="Pose"
                        description="Pick a preset pose or describe your own."
                        options={POSE_PRESETS}
                        value={state.pose}
                        onSelect={(option) => handleSelect("pose", option)}
                        placeholder="Search poses"
                      />
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <SearchableDropdown
                        label="Lighting"
                        description="Select lighting moods or add your own twist."
                        options={LIGHTING_PRESETS}
                        value={state.lighting}
                        onSelect={(option) => handleSelect("lighting", option)}
                        placeholder="Search lighting"
                      />
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <SearchableDropdown
                        label="Background"
                        description="Choose a setting or craft your own scene."
                        options={BACKGROUND_PRESETS}
                        value={state.background}
                        onSelect={(option) => handleSelect("background", option)}
                        placeholder="Search backgrounds"
                      />
                    </div>
                  </div>
                </section>

                {error && (
                  <div className="mt-6 border-l-2 border-primary-500 py-1 pl-3 font-Mulish text-sm text-secondary">
                    {error}
                  </div>
                )}

                <div className="mt-6 flex flex-col items-stretch justify-end gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setState({ ...INITIAL_PERSONA_STATE });
                      setFinalPrompt("");
                      setAiEnriched(false);
                      setCopied(false);
                      setError("");
                      setShowGeneratingModal(false);
                      setSuccessMessage("");
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                  >
                    <RotateCcwIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleGeneratePrompt}
                    disabled={
                      loading ||
                      !state.gender ||
                      !state.age ||
                      (state.age === "Custom" && !state.customAge) ||
                      !state.ethnicity ||
                      !state.complexion
                    }
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${
                      loading ||
                      !state.gender ||
                      !state.age ||
                      (state.age === "Custom" && !state.customAge) ||
                      !state.ethnicity ||
                      !state.complexion
                        ? "cursor-not-allowed bg-primary-100 text-primary-800"
                        : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                    }`}
                  >
                    {loading ? "Creating the prompt..." : `Generate prompt (${PROMPT_CREDITS} credit)`}
                  </button>
                </div>
              </div>

              <aside className="space-y-4 lg:sticky lg:top-0">
                <div>
                  <h2 className="mb-2 font-Mulish font-semibold text-lg text-secondary">
                    Prompt preview
                  </h2>
                  <p className="font-Mulish text-sm text-graphite">
                    Choose a look and the person, then generate a prompt.
                  </p>
                </div>

                <div className="flex min-h-[220px] flex-col rounded-2xl bg-primary-50 p-4">
                  {aiEnriched && finalPrompt ? (
                    <span className="mb-3 w-fit rounded-full bg-white px-3 py-1 font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                      Enriched
                    </span>
                  ) : null}
                  {finalPrompt ? (
                    <p className="whitespace-pre-line break-words font-Mulish text-sm text-secondary">
                      {finalPrompt}
                    </p>
                  ) : (
                    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
                      <p className="font-Mulish font-semibold text-lg text-secondary">No prompt yet</p>
                      <p className="mt-1 font-Mulish text-sm text-graphite">Your prompt will show up here</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 sm:gap-3">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={() => handleCopyPrompt()}
                      disabled={!finalPrompt}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 font-Mulish text-sm ${
                        finalPrompt
                          ? "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
                          : "cursor-not-allowed border-transparent bg-primary-100 text-primary-800"
                      }`}
                    >
                      <CopyIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {copied ? "Copied" : "Copy"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (onSelectPrompt && finalPrompt) {
                          onSelectPrompt(finalPrompt);
                          setShowFashionPromptBuilder(false);
                        }
                      }}
                      disabled={!finalPrompt}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 font-Mulish text-sm ${
                        finalPrompt
                          ? "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
                          : "cursor-not-allowed border-transparent bg-primary-100 text-primary-800"
                      }`}
                    >
                      <WandSparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Use
                    </button>
                  </div>

                  <p className="font-Mulish text-sm text-graphite">
                    Gender, age, ethnicity, and complexion are required. Change a field and generate again for a new prompt.
                  </p>
                </div>
              </aside>
            </div>
          </div>
          </>): (<div className={`flex flex-col w-full ${isModal ? 'h-full min-h-0 py-4 sm:py-6' : 'min-h-[calc(100vh-3rem)]'} items-center justify-center px-3 sm:px-4 ${isModal ? '' : 'pt-4 pb-4'}`}>
                <div className={`rounded-2xl bg-primary-50 p-6 text-center ${isModal ? "w-full max-w-md" : "w-full max-w-lg"}`}>
                  <p className="font-Mulish font-semibold text-lg text-secondary">Sign in to build a prompt</p>
                  <p className="mt-1 font-Mulish text-sm text-graphite">Sign in to describe a model and use the prompt.</p>
                  <button
                    type="button"
                    onClick={handleLoginRedirect}
                    className="mt-4 rounded-full bg-primary-700 px-6 py-2.5 font-Mulish font-semibold text-sm text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                  >
                    Sign in
                  </button>
                </div>
            </div>)}
      {showGeneratingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-[0_0_0_1px_var(--hairline)]">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary-200 border-t-primary-700" />
            <p className="mt-3 font-Mulish font-semibold text-secondary">Creating the prompt</p>
            <p className="mt-1 font-Mulish text-sm text-graphite">
              Writing a {activeModelMeta?.label} description. This takes a few seconds.
            </p>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="fixed top-4 sm:top-6 right-3 sm:right-6 z-40 max-w-[90%] sm:max-w-xs">
          <div className="rounded-2xl bg-white px-4 py-3 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)]">
            {successMessage}
          </div>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
        onClick={() => setShowFashionPromptBuilder(false)}
      >
        <div 
          className={`relative my-auto flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)] ${authenticated === true ? "h-auto max-h-[90vh]" : "h-[400px]"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - Fixed at top right */}
          <button
            type="button"
            onClick={() => setShowFashionPromptBuilder(false)}
            className="absolute right-4 top-4 z-10 rounded-full border border-hairline bg-white p-2 text-primary-700"
            aria-label="Close modal"
            title="Close"
          >
            <XIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>

          {/* Header with Title */}
          <div className="shrink-0 border-b border-hairline px-6 py-4 pr-14">
            <h2 className="font-Mulish font-semibold text-lg text-secondary">Fashion Prompt Builder</h2>
          </div>
          
          {/* Scrollable Content - Only scroll if authenticated, otherwise center content */}
          <div className={`flex-1 min-h-0 ${authenticated === true ? 'overflow-y-auto' : 'overflow-hidden flex items-center justify-center'}`}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  return content;
};

export default FashionPromptBuilder;
